/**
 * Universal Onboarding Flow
 * 
 * Works with both AuthContext and CustomerAuthContext
 * by reading directly from localStorage
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { dispatchOnboardingFlowComplete, resetOnboardingCompleteGate } from '../../lib/onboardingLifecycle';
import { WelcomeAnimation } from './WelcomeAnimation';
import { TourPrompt } from './TourPrompt';
import { PlatformTour } from './PlatformTour';
import { ComoAssistant } from './ComoAssistant';

type OnboardingState = 'welcome' | 'tour-prompt' | 'tour' | 'complete';

interface User {
  id: string;
  email?: string;
  first_name?: string;
  name?: string;
}

export function OnboardingFlowUniversal() {
  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const lastCompleteDispatchRef = useRef(false);

  useEffect(() => {
    if (!user?.id) {
      lastCompleteDispatchRef.current = false;
      resetOnboardingCompleteGate();
      return;
    }
    if (onboardingState !== 'complete') {
      return;
    }
    if (lastCompleteDispatchRef.current) {
      return;
    }
    lastCompleteDispatchRef.current = true;
    dispatchOnboardingFlowComplete();
  }, [user?.id, user, onboardingState]);

  useEffect(() => {
    console.log('🤖 COMO: OnboardingFlowUniversal mounted');
    
    // Try to get user from localStorage (works for both auth systems)
    const getUserFromStorage = () => {
      try {
        // Try all possible user keys
        const keys = ['coremonit_user', 'customer_user', 'user'];
        
        for (const key of keys) {
          const storedUser = localStorage.getItem(key);
          console.log(`🤖 COMO: Checking ${key}:`, storedUser ? 'Found' : 'Not found');
          
          if (storedUser) {
            const parsed = JSON.parse(storedUser);
            console.log(`🤖 COMO: ${key} ID:`, parsed.id);
            return parsed;
          }
        }
        
        console.log('🤖 COMO: No user found in localStorage');
        return null;
      } catch (e) {
        console.error('🤖 COMO: Error parsing user from localStorage:', e);
        return null;
      }
    };
    
    const storedUser = getUserFromStorage();
    setUser(storedUser);
    
    if (!storedUser) {
      console.log('🤖 COMO: No user - component will not render');
      return;
    }
    
    console.log('🤖 COMO: User found:', storedUser.email || storedUser.id);
    
    // Check if this is first login
    const hasSeenWelcome = localStorage.getItem(`welcome_seen_${storedUser.id}`);
    const hasCompletedTour = localStorage.getItem(`tour_completed_${storedUser.id}`);
    
    console.log('🤖 COMO: Onboarding state check:');
    console.log('   - welcome_seen:', hasSeenWelcome || 'No');
    console.log('   - tour_completed:', hasCompletedTour || 'No');
    
    if (!hasSeenWelcome) {
      // First time user - show welcome
      console.log('🤖 COMO: First time user - will show welcome animation');
      setOnboardingState('welcome');
    } else if (!hasCompletedTour) {
      const hasSkippedTour = localStorage.getItem(`tour_skipped_${storedUser.id}`);
      if (hasSkippedTour) {
        console.log('🤖 COMO: Tour was skipped earlier - not showing prompt');
        setOnboardingState('complete');
      } else {
        console.log('🤖 COMO: Welcome seen but tour not finished - showing tour prompt');
        setOnboardingState('tour-prompt');
      }
    } else {
      // Completed everything
      console.log('🤖 COMO: Tour already completed');
      setOnboardingState('complete');
    }
    
    // Listen for storage changes (when user logs in/out)
    const handleStorageChange = () => {
      const updatedUser = getUserFromStorage();
      setUser(updatedUser);
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for localStorage changes within same tab
    const interval = setInterval(() => {
      const currentUser = getUserFromStorage();
      if (JSON.stringify(currentUser) !== JSON.stringify(storedUser)) {
        setUser(currentUser);
      }
    }, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []); // Empty dependency array - only run on mount

  const handleWelcomeComplete = () => {
    if (user) {
      localStorage.setItem(`welcome_seen_${user.id}`, 'true');
      setOnboardingState('tour-prompt');
    }
  };

  const handleStartTour = () => {
    setOnboardingState('tour');
  };

  const handleSkipTour = () => {
    if (user) {
      localStorage.setItem(`tour_skipped_${user.id}`, 'true');
    }
    setOnboardingState('complete');
  };

  const handleTourComplete = () => {
    if (user) {
      localStorage.setItem(`tour_completed_${user.id}`, 'true');
    }
    setOnboardingState('complete');
  };

  if (!user) return null;

  // Extract first name from user object (support both formats)
  const firstName = user.first_name || user.name?.split(' ')[0] || user.email?.split('@')[0] || 'User';
  const isFirstLogin = !localStorage.getItem(`welcome_seen_${user.id}`);

  const tree = (
    <>
      {/* Welcome Animation */}
      {onboardingState === 'welcome' && (
        <WelcomeAnimation
          firstName={firstName}
          isFirstLogin={isFirstLogin}
          onComplete={handleWelcomeComplete}
        />
      )}

      {/* Tour Prompt */}
      {onboardingState === 'tour-prompt' && (
        <TourPrompt
          firstName={firstName}
          onStartTour={handleStartTour}
          onSkipTour={handleSkipTour}
        />
      )}

      {/* Platform Tour */}
      {onboardingState === 'tour' && (
        <PlatformTour
          onComplete={handleTourComplete}
          onSkip={handleSkipTour}
        />
      )}

      {/* COMO Assistant - Always Available */}
      <ComoAssistant />
    </>
  );

  // Portal to document.body so Radix dialog scroll-lock / inert on #root cannot block tour clicks
  // while a lower z-index modal is open (see dialog z-index in components/ui/dialog.tsx).
  if (typeof document === 'undefined') return tree;

  return createPortal(tree, document.body);
}